import React, { useState } from 'react';
import { authAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../utils/getErrorMessage';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isDev = process.env.NODE_ENV !== 'production';

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authAPI.login({ username, password });
      const { token, refreshToken, user } = response.data;
      localStorage.setItem('token', token);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      navigate('/');
    } catch (err: unknown) {
      setError(getErrorMessage(err, '登录失败，请检查用户名和密码'));
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = () => {
    setUsername('admin');
    setPassword('admin123');
    setTimeout(() => {
      const form = document.getElementById('login-form') as HTMLFormElement;
      if (form) form.requestSubmit();
    }, 100);
  };

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      {/* 顶部品牌条 */}
      <header className="border-b border-hairline-soft">
        <div className="max-w-content mx-auto px-6 py-4 flex items-center gap-2">
          <span className="w-8 h-8 rounded-md bg-primary-500 grid place-items-center text-white">
            <span className="text-base">🏸</span>
          </span>
          <span className="text-title-md font-semibold text-ink">橙羽体育</span>
        </div>
      </header>

      {/* 主体：左文案右表单（Cal 风格 7/5 split） */}
      <div className="flex-1 grid lg:grid-cols-12 max-w-content mx-auto w-full">
        {/* 左：欢迎文案（Cal Sans 风格大字号 + 负字距） */}
        <section className="lg:col-span-7 px-6 lg:px-12 py-12 lg:py-24 flex flex-col justify-center">
          <h1 className="text-[48px] lg:text-[64px] font-semibold leading-[1.05] tracking-[-0.04em] text-ink">
            排课、出勤、教研，<br />
            <span className="text-primary-500">一站搞定</span>
          </h1>
          <p className="mt-6 text-body-md text-body max-w-md">
            羽毛球培训管理系统，支持课程批量创建、客户分阶管理、教练教研协作、实时数据统计。
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            <span className="badge badge-blue">📅 智能排课</span>
            <span className="badge badge-orange">🎯 客户管理</span>
            <span className="badge badge-violet">📚 教案知识图谱</span>
            <span className="badge badge-green">💰 财务统计</span>
          </div>
        </section>

        {/* 右：登录表单 */}
        <section className="lg:col-span-5 px-6 lg:px-12 py-12 lg:py-24 flex items-center">
          <div className="w-full max-w-sm mx-auto">
            <div className="card p-8">
              <h2 className="text-title-lg text-ink mb-1">登录</h2>
              <p className="text-body-sm text-muted mb-6">用账号密码进入系统</p>

              <form id="login-form" onSubmit={handleLogin} className="space-y-4">
                {error && (
                  <div className="text-[13px] bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="username" className="block text-[13px] font-medium text-ink mb-1.5">
                    用户名
                  </label>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="input-field"
                    placeholder="请输入用户名"
                    required
                    autoComplete="username"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-[13px] font-medium text-ink mb-1.5">
                    密码
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field"
                    placeholder="请输入密码"
                    required
                    autoComplete="current-password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full mt-2"
                >
                  {loading ? '登录中...' : '登录'}
                </button>
              </form>

              {isDev && (
                <button
                  type="button"
                  onClick={handleDemoLogin}
                  disabled={loading}
                  className="btn-secondary w-full mt-3 text-[13px]"
                >
                  使用演示账户快速登录（仅开发）
                </button>
              )}

              <p className="text-[12px] text-muted-soft text-center mt-6 leading-relaxed">
                教练账号请联系管理员创建<br />
                忘记密码可联系管理员重置
              </p>
            </div>

            <p className="text-[12px] text-muted-soft text-center mt-4">
              橙羽体育 © {new Date().getFullYear()}
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Login;
